import { Position } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import IconButton from '@ui/buttons/IconButton'
import cx from 'classnames'
import React from 'react'
import { size } from 'lodash'
import cloneDeep from 'lodash/cloneDeep'
import { Icon, Flex } from '@ui'
import forEachNode, { LocationTreeNodeT } from '../../../../hooks/utils/forEachNode'
import css from './renderTreeContents.module.scss'

export enum LocationNodeType {
  Location = 'Location',
  City = 'City',
  Building = 'Building',
  Floor = 'Floor',
}

type RenderNode<SettingsT> = (node: LocationTreeNodeT, settings: SettingsT) => void
type NodeComponents = { [key in LocationNodeType]: RenderNode<RenderLocationCallbacks> }

const renderNodeLocation: RenderNode<RenderLocationCallbacks> = (node) => {
  const locationName = node.label

  node.label = (
    <div className={`${css.line_text} uppercase`}>
      {locationName} <hr className="line" />
    </div>
  )
}

const renderNodeCity: RenderNode<RenderLocationCallbacks> = (node, callbacks) => {
  const cityName = node.label as string
  node.icon = <Icon name="city" />
  node.secondaryLabel = (
    <IconButton
      disabled={node.disabled}
      className={css.selected_plus}
      iconName={node.isSelected || node.disabled ? 'check' : 'plus'}
      onClick={(e) => {
        e.stopPropagation()
        callbacks.handleSelectNodeClick(node)
      }}
    />
  )
  node.label = (
    <div className="flex items-center">
      <Tooltip2 disabled={!node.disabled} content={disabledTooltip} position={Position.TOP}>
        <>
          <div className={`${css.render_city_title} flex justify-between`}>{cityName}</div>
          <div className={`${css.address} uppercase`}>{node?.nodeData?.city?.state || ''}</div>
        </>
      </Tooltip2>
      <span className={`ml-auto ${css.render_count}`}>{size(node.childNodes)} Bldg</span>
    </div>
  )
}

const renderNodeBuilding: RenderNode<RenderLocationCallbacks> = (node, callbacks) => {
  const buildingName = node.label as string
  const building = node.nodeData?.building

  node.className = 'building-segment'
  node.icon = <Icon name="building" />
  node.secondaryLabel = (
    <IconButton
      disabled={node.disabled}
      className={css.selected_plus}
      iconName={node.isSelected || node.disabled ? 'check' : 'plus'}
      onClick={(e) => {
        e.stopPropagation()
        callbacks.handleSelectNodeClick(node)
      }}
    />
  )
  node.hasCaret = !node.disabled
  node.label = (
    <div className="flex items-center">
      <Tooltip2 disabled={!node.disabled} content={disabledTooltip} position={Position.TOP}>
        <>
          <div className={cx('flex justify-between', [css.render_tree_title])}>{buildingName}</div>
          <div className={`${css.address} ${css.truncate} truncate`}>{building?.address}</div>
        </>
      </Tooltip2>
      <span className={`ml-auto ${css.render_count}`}> {size(node.childNodes)} Flrs</span>
    </div>
  )
}

const renderNodeFloor: RenderNode<RenderLocationCallbacks> = (node) => {
  const floorName = node.label

  node.icon = <Icon name="map-marked-alt" />
  node.secondaryLabel = (
    <Icon
      className={cx('fas', {
        [css.selected_check]: node.isSelected || node.disabled,
        [css.selected_plus]: !node.isSelected,
      })}
      name={node.isSelected || node.disabled ? 'check' : 'plus'}
    />
  )
  node.label = (
    <Flex align="baseline">
      <Tooltip2 disabled={!node.disabled} content={disabledTooltip} position={Position.TOP}>
        <div className={css.render_tree_title}>{floorName}</div>
      </Tooltip2>
      <div className="ml-auto"></div>
    </Flex>
  )
}

const renderNodeSelectedCity: RenderNode<RenderLocationCallbacks> = (node, callbacks) => {
  const cityName = node.label as string
  node.icon = <Icon name="city" />
  node.secondaryLabel = (
    <IconButton
      className={css.selected_plus}
      iconName="remove"
      onClick={(e) => {
        e.stopPropagation()
        callbacks.handleSelectNodeClick(node)
      }}
    />
  )
  node.label = (
    <div className="flex items-center">
      <div>
        <div className={`${css.render_city_title} flex justify-between`}>{cityName}</div>
        <div className={`${css.address} uppercase`}>{node?.nodeData?.city?.state || ''}</div>
      </div>
      <span className={`ml-auto ${css.render_count}`}>{size(node.childNodes)} Bldg</span>
    </div>
  )
}

const renderNodeSelectedBuilding: RenderNode<RenderLocationCallbacks> = (node, callbacks) => {
  const buildingName = node.label as string
  const building = node.nodeData?.building

  node.className = 'building-segment'
  node.icon = <Icon name="building" />
  node.secondaryLabel = (
    <IconButton
      className={css.selected_plus}
      iconName="remove"
      onClick={(e) => {
        e.stopPropagation()
        callbacks.handleSelectNodeClick(node)
      }}
    />
  )
  node.label = (
    <div className="flex items-center">
      <div>
        <div className={`${css.render_tree_title} flex justify-between`}>{buildingName}</div>
        <div className={`${css.address} ${css.truncate} truncate`}>{building?.address}</div>
      </div>
      <span className={`ml-auto ${css.render_count}`}> {size(node.childNodes)} Flrs</span>
    </div>
  )
}

const renderNodeSelectedFloor: RenderNode<RenderLocationCallbacks> = (node, callbacks) => {
  const floorName = node.label

  node.icon = <Icon name="map-marked-alt" />
  node.secondaryLabel = (
    <IconButton
      className={css.selected_plus}
      iconName="remove"
      onClick={(e) => {
        e.stopPropagation()
        callbacks.handleSelectNodeClick(node)
      }}
    />
  )
  node.label = (
    <Flex align="baseline">
      <div className={css.render_tree_title}>{floorName}</div>
      <div className="ml-auto"></div>
    </Flex>
  )
}

const renderNodeSelectedLocation: RenderNode<RenderLocationCallbacks> = (node) => {
  const locationName = node.label

  node.label = (
    <div className={`${css.line_text} uppercase text-12px font-bold`}>
      {locationName} <hr className="line" />
    </div>
  )
}

const locationNodeComponents: NodeComponents = {
  [LocationNodeType.City]: renderNodeCity,
  [LocationNodeType.Building]: renderNodeBuilding,
  [LocationNodeType.Floor]: renderNodeFloor,
  [LocationNodeType.Location]: renderNodeLocation,
}

const selectedLocationNodeComponents: NodeComponents = {
  [LocationNodeType.City]: renderNodeSelectedCity,
  [LocationNodeType.Building]: renderNodeSelectedBuilding,
  [LocationNodeType.Floor]: renderNodeSelectedFloor,
  [LocationNodeType.Location]: renderNodeSelectedLocation,
}

type RenderLocationCallbacks = {
  handleSelectNodeClick: (treeNode: LocationTreeNodeT) => void
}

export const renderLocationTreeContents = (
  treeNodes: LocationTreeNodeT[],
  callbacks: RenderLocationCallbacks
): LocationTreeNodeT[] => {
  // it's necessary to mutate the state
  const clonedTreeNodes = cloneDeep(treeNodes)

  forEachNode((node) => {
    if (node.nodeData) {
      locationNodeComponents[node.nodeData.nodeType]?.(node, callbacks)
    }
  }, clonedTreeNodes)

  return clonedTreeNodes
}

export const renderSelectedLocationTreeContents = (
  treeNodes: LocationTreeNodeT[],
  callbacks: RenderLocationCallbacks
): LocationTreeNodeT[] => {
  // it's necessary to mutate the state
  const clonedTreeNodes = cloneDeep(treeNodes)

  forEachNode((node) => {
    if (node.nodeData) {
      selectedLocationNodeComponents[node.nodeData.nodeType]?.(node, callbacks)
    }
  }, clonedTreeNodes)

  return clonedTreeNodes
}

const disabledTooltip = 'Already selected'
